import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash-es';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchConfiguratorConfig } from '@/store/slices/configurator-config';
import { handoffCurrencySwitch, resolveMintCurrency } from '@/utils/region-currency';
import {
  clearRecommendedCampaigns,
  clearSessionData,
  isConfiguratorSessionResumable,
  SessionStatus,
  sessionEnsure,
  sessionFetchRecommendedCampaigns,
  sessionSave,
  sessionSetCurrency,
  sessionSetRecommendedCampaign,
} from '@/store/slices/session';
import { CampaignItemType, type Campaign, type DefaultItem } from '@/components/modules/spark/configurator/types';
import {
  applySpeiCheckoutEvent,
  applyStripeCheckoutEvent,
  type CheckoutProvider,
  type SpeiCheckoutAccounting,
  type SpeiCheckoutEvent,
  type StripeCheckoutAccounting,
  type StripeCheckoutEvent,
} from '@/utils/session-checkout-accounting';
import { preferredHandoffRegion } from '@/nova/config/localeRegion';
import { toBackendLang } from '@/nova/config/langCodeMap';
import {
  markStartCampaignPreselectDone,
  markStartCurrencyApplied,
  markStartOrApplied,
  readStartCurrencyApplied,
  readStartMarketHandoff,
  readStartOrApplied,
  startCampaignPreselectDone,
} from '@/utils/start-properties-market';

import type {
  ArtStyleOption,
  Bundle,
  ColorOption,
  Country,
  CountryGroup,
  Feature,
  FeatureTier,
  PlayerOption,
  Theme,
} from '../lib/domain-types';
import {
  availableStyles,
  transformCatalog,
  transformRegions,
  transformTrafficOptions,
  type Raw,
} from '../data/api';
import { buildPreviewUrl } from '../data/preview';
import { currencySymbol } from '../lib/currency';
import { computeBilling, interpretVoucher, type Billing, type VoucherResult } from '../data/billing';
import {
  buildCampaignOptions,
  campaignFeatureTiers,
  campaignTierPriceMap,
  catalogTierPriceMap,
  mergeCampaigns,
  type CampaignOption,
} from '../lib/campaigns';
import {
  clearStartSelectedPackage,
  clearStartTemplate,
  clearStartVoucherCode,
  readStartSelectedPackage,
  readStartTemplate,
  readStartVoucherCode,
} from '../lib/startProperties';

/** Base path the wizard is mounted under (see router.tsx). */
export const CONFIGURATOR_BASE = '/easy-setup';

/**
 * Statuses whose sessions still accept a `configuration_data` body.
 *
 * `POST /session/save` overwrites `data` wholesale and validates it against the session's CURRENT
 * status, so a `configuration_data` body sent to a session that has advanced into the
 * post-payment half (which now owns `customization_data`) is rejected with
 * `INVALID_SESSION_STATE`. That boundary is now crossed from INSIDE this wizard:
 * `POST /session/payment-acknowledged` is a real state transition, fired both when the card
 * charge clears and on entry for a session that arrives already paid. Every save path in this
 * store therefore gates on this set.
 *
 * `payment_processing` / `payment_failed` stay savable: the session has not left the
 * configuration half in either (nothing has created `customization_data`), and the home
 * retry-payment banner deep-links a `payment_failed` session straight back into this wizard,
 * where blocking saves would silently drop the buyer's edits.
 */
const SAVABLE_SESSION_STATUSES: ReadonlySet<SessionStatus> = new Set([
  SessionStatus.configuration,
  SessionStatus.payment_processing,
  SessionStatus.payment_failed,
]);

export interface WizardStep {
  key: 'theme' | 'colors' | 'review' | 'payment';
  name: string;
  path: string;
}

/**
 * The v0 flow is the SHORT path: pick a theme, then colours & art style, then review the
 * bill and pay. The Features (gamification packs) and Players (audience) steps of v3 are
 * intentionally skipped — the session keeps their safe defaults (every feature at its Off
 * tier = the Basic baseline; audience = the first option), which `buildConfigurationData`
 * and `saveNow` commit automatically, so the session still prices + checks out correctly.
 */
export const WIZARD_STEPS: WizardStep[] = [
  { key: 'theme', name: 'Theme', path: `${CONFIGURATOR_BASE}/theme` },
  { key: 'colors', name: 'Colors & Art Style', path: `${CONFIGURATOR_BASE}/colors` },
  { key: 'review', name: 'Payment review', path: `${CONFIGURATOR_BASE}/review` },
  { key: 'payment', name: 'Payment', path: `${CONFIGURATOR_BASE}/payment` },
];

/** Wizard step key → API step name used by session/save. Theme + colours are both the
 *  backend `design` step; review + payment are the final `live` step (v0 has no
 *  `engagement`/`traffic` steps — those defaults ride along in `configuration_data`). */
const API_STEP: Record<string, string> = {
  theme: 'design',
  colors: 'design',
  review: 'live',
  payment: 'live',
};

/** Default region: International (USD). */
const DEFAULT_COUNTRY = 'INT';

/** Per-market recommended-campaigns fetch attempts before giving up (don't hammer a down API).
 *  Shared by the reco effect and the pack pre-select effect (which stops waiting once a market's
 *  recos have either succeeded or exhausted these attempts). */
const RECO_MAX_ATTEMPTS = 3;

/** Per-session localStorage key marking that the user has configured step 2 (picked a pack
 *  or edited a feature). Until then step 2 opens at the Basic baseline (all Off, no pack). */
const ENGAGED_KEY = (sessionId: string): string => `cfg-v3-engaged:${sessionId}`;

/** Per-session localStorage key marking that the user is explicitly on the Custom selection.
 *  Custom is STICKY: once entered (a manual feature edit, or confirming the "switch to Custom"
 *  modal) it stays Custom even if those manual tiers happen to equal a pack's defaults. The ONLY
 *  exit is explicitly applying a pack (or the Basic card). It therefore can't be derived by
 *  structurally comparing tiers to the bound campaign; it's an own, persisted flag. */
const CUSTOM_KEY = (sessionId: string): string => `cfg-v3-custom:${sessionId}`;

/** Per-session localStorage key marking that the user has pressed "Confirm package selection" in
 *  the PackageChooser AT LEAST ONCE. Distinct from ENGAGED_KEY: engagement also fires on editing a
 *  feature tier or restoring a prior session, whereas this latches ONLY on the chooser's apply
 *  funnel (`applyCampaign`). The "Find my pack" reveal (shine + grow) plays until this is set; once
 *  set, the chooser shows the selected pack directly with no animation. One-way per session. */
const CONFIRMED_KEY = (sessionId: string): string => `cfg-v3-confirmed:${sessionId}`;

type Status = 'loading' | 'ready' | 'error';

const isOff = (level: string): boolean => level.toLowerCase() === 'off';

/** The Off tier id for a feature (falls back to its first tier). */
function offTierId(feature: Feature): number | undefined {
  return (feature.tiers.find((t) => isOff(t.level)) ?? feature.tiers[0])?.id;
}

interface Selection {
  themeId: string | null;
  /** Theme shown in the large featured slot (only moved by the catalog). */
  featuredThemeId: string | null;
  colorId: string | null;
  styleId: string | null;
}

interface ConfiguratorContextValue {
  status: Status;
  themes: Theme[];
  /** Gamification features (step 2). */
  features: Feature[];
  /** Gamification packages/bundles (step 2). */
  bundles: Bundle[];
  /** Audience/traffic options for the selected market (step 3). */
  playerOptions: PlayerOption[];
  /** Selected step-3 traffic/audience option id (PlayerOption.id), or null. */
  trafficOptionId: string | null;
  /** Select a step-3 traffic/audience option (persists to the session). */
  setTrafficOption: (id: string) => void;
  selection: Selection;
  /** The currently selected theme (drives the Colors/Styles lists). */
  selectedTheme: Theme | null;
  /** The theme rendered in the featured slot. */
  featuredTheme: Theme | null;
  colors: ColorOption[];
  selectedColor: ColorOption | null;
  styles: ArtStyleOption[];
  selectedStyle: ArtStyleOption | null;
  previewUrl: string;
  /** featureId → selected tier id (step 2). */
  featureTiers: Record<number, number>;
  /** Applied package/bundle id, or null when "custom"/none. */
  packageId: number | null;
  /** Index of the bundle the user picked (persists into "custom"); null = never picked. */
  pickedIndex: number | null;
  /** Email address read back from the loaded session. */
  emailAddress: string;
  /** All countries (flattened); coming-soon markets are included but not selectable. */
  countries: Country[];
  /** Countries grouped by region (for the picker). */
  countryGroups: CountryGroup[];
  /** Currently selected country. */
  country: Country | null;
  /** Selected operating-region code (e.g. "INT" / "MEX"). Present even before the catalog has
   *  resolved the `country` view-model; the checkout uses it to resolve the rails on offer. */
  countryCode: string;
  /** Currency the session is priced in (`configuration_data.currency`, e.g. USD / MXN). */
  currency: string;
  /** Display symbol for the active currency. */
  currencySymbol: string;
  /** Currencies offered by the account-menu currency selector (the tenant's active markets). */
  currencies: string[];
  /** Set the session currency via `/set-currency`. The backend re-prices the bound + recommended
   *  campaigns; a real re-price, not a relabel. Independent of the operating region. */
  setCurrency: (code: string) => void;
  /** Whether the country picker overlay is open. */
  countryPickerOpen: boolean;
  openCountryPicker: () => void;
  closeCountryPicker: () => void;
  /** Select a country/region (switches currency + persists). */
  setCountry: (code: string) => void;
  /** Select a theme without promoting it to the featured slot. */
  setTheme: (id: string) => void;
  /** Select a theme and promote it to the featured slot (used by the catalog). */
  chooseTheme: (id: string) => void;
  setColor: (id: string) => void;
  setStyle: (id: string) => void;
  /** Set one feature's tier (switches the selection to "custom"). */
  setFeatureTier: (featureId: number, tierId: number) => void;
  /** Apply a whole package: set each feature to its bundle tier (others Off). */
  applyBundle: (bundle: Bundle) => void;
  /** Apply the "Basic" (included) pack: every feature Off, no bundle. */
  applyBasicPack: () => void;
  /** Commit a whole custom tier selection at once (mobile custom pack). */
  applyCustomTiers: (tiers: Record<number, number>) => void;
  /** Last saved custom pack configuration (null until first save). */
  customTiers: Record<number, number> | null;
  /**
   * Server-computed billing breakdown for step 4, derived reactively from the live
   * session (`state.session`) + active currency. Null until the session has loaded.
   * Updates automatically as `sessionSave` responses land, so the total tracks the
   * latest selections.
   */
  billing: Billing | null;
  /** Features-only total for the step-2 footer (catalog sum when Custom, else campaign). */
  featuresTotal: number;
  /** All selectable campaigns (Basic + recommended) + a synthesized "Custom" entry. */
  campaigns: CampaignOption[];
  /** The currently applied campaign option (Custom when the user deviates). */
  activeCampaign: CampaignOption | null;
  /** True when the session items deviate from the active campaign's defaults. */
  isCustomPack: boolean;
  /** True once the user has pressed "Confirm package selection" in the chooser (one-way, persisted
   *  per-session). Gates the chooser's "Find my pack" reveal: shine + grow while false, the
   *  selected pack shown directly once true. */
  hasConfirmedPack: boolean;
  /** Apply a whole campaign: set the session campaign + sync feature tiers. */
  applyCampaign: (option: CampaignOption) => void;
  /**
   * Persist the full configuration immediately. Wired to every "Continue" button / step
   * transition so each step is saved as the user advances (not only when a value changes,
   * which is what the background autosave covers). Also commits the default audience option
   * and the latest `lastStep`. No-op while a save / set-recommended-campaign request is in
   * flight, so it can't race-clobber that request.
   */
  saveNow: () => void;
  /**
   * Apply (or replace) a voucher code from the checkout review. Persists it as the session's
   * `voucher_code`, awaits the backend re-evaluation, and resolves with the outcome (applied, or a
   * rejection reason). The echoed voucher also updates `billing` reactively (the discount line +
   * `dueToday`). Bypasses the `saveNow` in-flight skip so an explicit apply always round-trips.
   */
  applyVoucher: (code: string) => Promise<VoucherResult>;
  /** Remove the applied voucher (persists an empty `voucher_code`). Resolves once cleared. */
  removeVoucher: () => Promise<VoucherResult>;
  /** True while an `applyVoucher` / `removeVoucher` round-trip is in flight (drives the Apply
   *  spinner + disables the controls). Distinct from the global `session.loading`. */
  voucherPending: boolean;
  /**
   * Record progress through the SPEI (bank transfer) checkout onto the SESSION, so the funnel is
   * visible server-side and not only in the analytics stream. Merges into
   * `configuration_data.spei_checkout` and persists on a debounce, so the copy counters (which
   * can fire many times in a few seconds) cost one save, not one per click.
   *
   * Ported from the v0-mx store so base v0 can drive the shared `useCheckout` cross-flow; the
   * four members match `CheckoutStoreApi` name-for-name.
   */
  recordCheckout: (event: SpeiCheckoutEvent) => void;
  /** The same, for the CARD rail: merges into `configuration_data.stripe_checkout`. A separate
   *  accumulator because the rails fail differently (a transfer by never being made, a card
   *  loudly with an attempt count and an issuer reason). */
  recordCardCheckout: (event: StripeCheckoutEvent) => void;
  /** Remember which rail the buyer selected (`configuration_data.payment_method`), debounced, so a
   *  checkout they come back to opens on the method they committed to. */
  setPaymentMethod: (method: CheckoutProvider) => void;
  /** Record events and persist them RIGHT NOW, resolving when the save has settled. The debounced
   *  recorders are wrong for the last event of a checkout, which immediately advances the session
   *  (after which `configuration_data` can no longer be written). Never rejects. */
  flushCheckout: (event: CheckoutFlushEvent) => Promise<void>;
}

/** Events to merge and persist in one immediate save. Whichever rail is finishing supplies its
 *  own; passing both is legal and merges both. Structurally identical to the v0-mx store's, so
 *  `useConfigurator()` is assignable to the cross-flow `CheckoutStoreApi`. */
export interface CheckoutFlushEvent {
  spei?: SpeiCheckoutEvent;
  stripe?: StripeCheckoutEvent;
}

export type { SpeiCheckoutEvent, StripeCheckoutEvent };

const ConfiguratorContext = createContext<ConfiguratorContextValue | null>(null);

export function ConfiguratorProvider({ children }: { children: ReactNode }): React.ReactElement {
  const dispatch = useAppDispatch();
  // Live config catalog + authenticated session come from the app's Redux store
  // (the same slices/thunks the config-driven Spark configurator uses) — NOT from
  // hardcoded demo fetches anymore.
  const configState = useAppSelector((s) => s.configuratorConfig, isEqual);
  const session = useAppSelector((s) => s.session, isEqual);
  const userEmail = useAppSelector((s) => s.authentication.user?.email);

  const [status, setStatus] = useState<Status>('loading');
  // Raw configurator response from the store; the catalog derives from it + currency.
  const raw = (configState.config as unknown as Raw | null) ?? null;
  const [selection, setSelection] = useState<Selection>({
    themeId: null,
    featuredThemeId: null,
    colorId: null,
    styleId: null,
  });
  // Step 2 selections (lifted here so a save can send the full configuration).
  const [featureTiers, setFeatureTiers] = useState<Record<number, number>>({});
  const [packageId, setPackageId] = useState<number | null>(null);
  const [pickedIndex, setPickedIndex] = useState<number | null>(null);
  // Saved "Configure your own" tier map (mobile) — survives pack switches.
  const [customTiers, setCustomTiers] = useState<Record<number, number> | null>(null);
  // Step 3 selection (traffic/audience option id), lifted here + persisted to the session.
  const [trafficOptionId, setTrafficOptionIdState] = useState<string | null>(null);
  // "Engaged" = the user has actively configured step 2 (picked a pack or edited a feature).
  // Until then the step shows the Basic baseline (all Off) + the "choose pack" card, ignoring
  // the backend's default pack binding. Persisted per-session in localStorage so it survives
  // reloads without racing the set-recommended-campaign POST that owns campaignId.
  const [engaged, setEngaged] = useState(false);
  // Whether the user is explicitly on Custom (sticky; see CUSTOM_KEY). Replaces the old
  // structural "do the tiers deviate from the bound pack?" derivation: a manual config that
  // coincidentally equals a pack must NOT snap back to that pack. Custom ends only when a pack
  // (or the Basic card) is explicitly applied.
  const [custom, setCustom] = useState(false);
  // Whether the user has pressed "Confirm package selection" in the chooser at least once (see
  // CONFIRMED_KEY). Latches the "Find my pack" reveal off: while false the chooser plays its
  // shine + grow reveal, once true it shows the selected pack directly. One-way per session.
  const [hasConfirmedPack, setHasConfirmedPack] = useState(false);

  // Selected country/region (drives currency) + picker overlay state.
  const [countryCode, setCountryCode] = useState<string>(DEFAULT_COUNTRY);
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);

  // Active UI language as the short ISO code ("en-US" -> "en"). Read from i18next (the
  // same source `<Text>` renders from) so the CMS-driven copy below (feature/tier names,
  // tier descriptions, highlights, traffic options) stays in lockstep with the static
  // copy: the `/configurator` response ships these strings per language, and the
  // transforms pick `lang` (falling back to English) instead of always reading `en`.
  const { i18n } = useTranslation();
  const lang = toBackendLang((i18n.language || 'en').split('-')[0]);

  // Countries + active currency, derived from the raw response + selection.
  const regions = useMemo(
    () => (raw ? transformRegions(raw, lang) : { countries: [], groups: [] }),
    [raw, lang],
  );
  // Active currency of this flow. The account-menu selector (UserMenu) and the region picker both
  // switch it server-side via `/set-currency`; we READ it back off the live session rather than
  // pinning a literal, because the session is what the backend prices the campaigns in, so the
  // symbols on screen can never disagree with the amounts. Until the session lands we show the
  // same currency the session WOULD
  // mint in (`resolveMintCurrency`: explicit hand-off currency, else the catalogue/static currency
  // for the hand-off region, else USD). Re-evaluated per render (not memoised) so the catalogue
  // arriving upgrades the placeholder. Changing the operating region re-prices it: `setCountry`
  // calls `setCurrency` to the new market's currency when it is catalogue-active.
  const currency = session.data.configuration_data?.currency || resolveMintCurrency(configState.config);
  // Currencies offered by the account-menu selector: the catalogue's `is_active` currencies (exactly
  // the set `/set-currency` accepts). No hardcoded market list — the backend is the authority. The
  // session's active currency is always included so it still shows before the catalogue loads (the
  // active list is then empty) or if it is somehow outside the catalogue set.
  const currencies = useMemo<string[]>(() => {
    const active = (configState.config?.currencies ?? [])
      .filter((c) => c.is_active !== false)
      .map((c) => c.code);
    return active.includes(currency) ? active : [currency, ...active];
  }, [configState.config, currency]);
  // Whether the loaded catalogue flags a currency `is_active` — exactly the set `/set-currency`
  // accepts (an inactive code is rejected). The region-reprice gates on this so it never dispatches
  // a change the backend would refuse. Returns false when the catalogue has not loaded (safe: skip).
  const isCatalogueActiveCurrency = (code: string): boolean =>
    (configState.config?.currencies ?? []).some((c) => c.code === code && c.is_active !== false);
  const catalog = useMemo(() => (raw ? transformCatalog(raw, currency, lang) : null), [raw, currency, lang]);
  // Step-3 (Players) options are region-scoped — re-derived when the market changes.
  const playerOptions = useMemo(
    () => (raw ? transformTrafficOptions(raw, countryCode, lang) : []),
    [raw, countryCode, lang],
  );

  // Which wizard step we're on → drives lastStep + stepsVisited in saves.
  const location = useLocation();
  const stepKey = location.pathname.split('/').filter(Boolean).pop() ?? 'design';
  const apiStep = API_STEP[stepKey] ?? 'design';
  const [stepsVisited, setStepsVisited] = useState<Record<string, boolean>>({
    design: true,
  });
  useEffect(() => {
    setStepsVisited((prev) => (prev[apiStep] ? prev : { ...prev, [apiStep]: true }));
  }, [apiStep]);

  // Latest values read inside the save effect without re-triggering it.
  const apiStepRef = useRef(apiStep);
  apiStepRef.current = apiStep;
  const stepsVisitedRef = useRef(stepsVisited);
  stepsVisitedRef.current = stepsVisited;
  // True only after a user-initiated selection (guards init + navigation).
  const dirtyRef = useRef(false);
  // The market the recommended campaigns were last SUCCESSFULLY fetched for. Marked only
  // on success (see the reco effect) so a transient API failure self-heals instead of
  // sticking the chooser empty for the whole session.
  const recoCountryRef = useRef<string | null>(null);
  // Holds the recommended-campaigns fetch while a language-CUED region (see the seed
  // effect) is being persisted. Recos are fetched by `session_id` alone — the backend
  // prices them for the region SAVED on the session — so fetching before the cued region
  // lands would return the default market's packs. Set when the cue is applied, released
  // once the surgical seed-save (below) has committed the region; not used when a country
  // was already saved (that path is already persisted).
  const recoHoldRef = useRef(false);
  // A recommended-campaigns fetch is in flight — prevents a double dispatch across the
  // pending → settled render cycle without optimistically marking the market done.
  const recoInflightRef = useRef(false);
  // Per-market fetch attempts — bounds retries so a persistently-down API isn't hammered.
  const recoAttemptsRef = useRef<Record<string, number>>({});
  // Bumped when a fetch settles to re-run the effect (retry on failure / settle on success).
  const [recoRetryTick, setRecoRetryTick] = useState(0);

  // The marketing-site voucher / promo code from the `startProperties` hand-off, captured once at
  // mount so it's stable across re-renders (and survives the sessionStorage stash being cleared
  // once the session durably carries it). Seeded onto the fresh mint and injected into every
  // `configuration_data` save so the backend applies it; the applied discount comes back on the
  // session's top-level `voucher` object and drives the checkout voucher line.
  const startVoucherRef = useRef<string | null>(readStartVoucherCode());

  // The user's own voucher edit from the checkout review, which OVERRIDES both the hand-off code and
  // whatever is already on the session. A three-state signal `buildConfigurationData` reads:
  //   null   = untouched this session → fall through to the hand-off / session precedence below;
  //   ''     = the user REMOVED the voucher → persist an explicit empty `voucher_code` to clear it;
  //   'CODE' = the user applied/replaced it → wins over everything.
  // `applyVoucher` / `removeVoucher` set it, then dispatch an explicit save (bypassing `saveNow`'s
  // in-flight skip) so the backend re-evaluates and echoes the applied `voucher`.
  const userVoucherRef = useRef<string | null>(null);

  // Once the live session durably carries the voucher code, consume the one-shot stash so it can't
  // re-apply to a LATER brand (a voucher is single-use; a second brand would just get a dead code).
  // EXCEPTION: while a hand-off override is still PENDING — the session holds an OLDER code and a
  // DIFFERENT hand-off code (`startVoucherRef`) is waiting to supersede it — keep the stash. The
  // checkout reads it via `readStartVoucherCode()` to apply the override on a deep-link straight to
  // payment (no intervening step-save persists it), so wiping it here would revert that buyer to the
  // stale voucher. Cleared as soon as the override lands (session code === hand-off) or there is no
  // hand-off this session.
  useEffect(() => {
    const sessionVoucher = session.data.configuration_data?.voucher_code ?? null;
    const overridePending = startVoucherRef.current != null && sessionVoucher !== startVoucherRef.current;
    if (sessionVoucher && !overridePending) clearStartVoucherCode();
  }, [session.data.configuration_data?.voucher_code]);

  // Bootstrap: fetch the public catalog + resume-or-create the session once.
  // `sessionEnsure` resumes a persisted or in-progress session and only mints when
  // there's genuinely none — so landing here directly (or re-entering) reuses the
  // existing build instead of stacking up empty sessions.
  useEffect(() => {
    if (!configState.config && !configState.loading) dispatch(fetchConfiguratorConfig());

    // A session may already be loaded from a prior flow — the magic-link sign-in loads the
    // link's OWN session, and a reload restores the last one. Only RESUME it here when it's a
    // genuine, still-editable configurator build (unpaid `configuration`, or status not yet
    // known). A session already PAST configuration (paid / customising / deployed / done — e.g.
    // a stale one resurfaced by reusing an OLD login link, whose embedded `session_id` points at
    // a previous, already-advanced build) is NOT resumable: the wizard would persist onto it and
    // the payment `/invoice` call would reject it with SESSION_INVALID_STATE ("not in
    // configuration state"). Drop it and ensure a fresh one — `sessionEnsure` adopts a genuine
    // pending build via `/check`, else mints. Clearing first flips `loaded` false synchronously
    // so the seed effect waits for the fresh session instead of latching the stale data.
    if (!session.loading) {
      const resumable = isConfiguratorSessionResumable(session);
      if (!resumable) {
        if (session.loaded) dispatch(clearSessionData());
        dispatch(sessionEnsure({ voucherCode: startVoucherRef.current }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Fetch the recommended campaigns once per market (they populate the pack chooser
  // alongside the session's own campaign). Recommendations are priced for the session's
  // COMMITTED market, so we only fetch when the session is idle — no pending edit
  // (`dirtyRef`) and no save/load in flight (`session.loading`). On a country change
  // the pending save flips `session.loading`, so this waits for the round-trip to
  // settle before fetching, instead of racing it (the optimistic `sessionSave.pending`
  // makes `session.data` an unreliable "committed market" signal). `session.loading`
  // is in the deps so the effect re-evaluates when the save settles.
  useEffect(() => {
    // Wait for the seed to settle `countryCode` (status 'ready') so we fetch for the
    // resolved market once, not the default then the hydrated one.
    if (status !== 'ready' || !session.loaded || !session.sessionId) return;
    if (dirtyRef.current || session.loading) return;
    // Wait for a language-cued region to be persisted before fetching (see recoHoldRef) —
    // otherwise the backend prices the recos for the not-yet-saved default market.
    if (recoHoldRef.current) return;
    if (recoInflightRef.current) return;
    if (recoCountryRef.current === countryCode) return;
    const market = countryCode ?? '';
    // Retry a transient failure a few times, then give up (don't hammer a down API).
    if ((recoAttemptsRef.current[market] ?? 0) >= RECO_MAX_ATTEMPTS) return;

    recoInflightRef.current = true;
    recoAttemptsRef.current[market] = (recoAttemptsRef.current[market] ?? 0) + 1;
    dispatch(sessionFetchRecommendedCampaigns({ id: session.sessionId }))
      .unwrap()
      .then(() => {
        // Mark the market fetched ONLY on success — a failed fetch stays retryable.
        recoCountryRef.current = countryCode;
        recoAttemptsRef.current[market] = 0;
      })
      .catch(() => {
        // Leave the market unmarked so the effect retries (bounded above) instead of
        // leaving the pack chooser permanently empty after one transient API glitch.
      })
      .finally(() => {
        recoInflightRef.current = false;
        // Re-run the effect now the fetch settled (retry on failure / settle on success).
        setRecoRetryTick((n) => n + 1);
      });
  }, [status, session.loaded, session.sessionId, session.loading, countryCode, dispatch, recoRetryTick]);

  // Surface a catalog load failure to the UI.
  useEffect(() => {
    if (configState.error) setStatus('error');
  }, [configState.error]);

  // Seed the wizard once the catalog AND session are available: hydrate the design +
  // feature selection from the saved session when present (so a returning user resumes
  // where they left off and the first edit doesn't overwrite the saved config with
  // catalog defaults), else fall back to catalog defaults. Proceeds on session error
  // too (degraded: catalog defaults, no persistence) so the wizard isn't stuck loading.
  const initedRef = useRef(false);
  // Set by the seed effect when a marketing-site template hand-off DROVE the design (overriding the
  // resumed session's saved design). Tells the design-seed save below to persist that override even
  // over a session that already has a design (which it would otherwise skip).
  const templateAppliedRef = useRef(false);
  useEffect(() => {
    if (initedRef.current || !catalog) return;
    // Seed once the session is loaded (hydrate) OR has terminally failed (degrade to
    // catalog defaults). Treat `error` as terminal only when no load is in flight —
    // the slice doesn't clear a previous error on the next sessionLoad.pending, so a
    // stale error + an in-flight retry must NOT latch defaults (which the first edit
    // would then persist over the real saved config).
    if (!session.loaded && !(session.error && !session.loading)) return;
    initedRef.current = true;

    const cd = session.loaded ? session.data.configuration_data : undefined;

    // Marketing-site template hand-off (durable keys stashed at boot): a pre-picked seed / skin /
    // art style. When present it is an EXPLICIT fresh design choice and takes PRECEDENCE over the
    // resumed session's saved design — arriving with `?startProperties=…template…` is a deliberate
    // "use this design" signal, so it must win over whatever half-built session was there. Absent,
    // the saved session design wins, else catalogue defaults. Read once and consumed at the end of
    // this effect so a reload / later brand doesn't re-apply it.
    const template = readStartTemplate();

    // Precedence per field: template → saved session → catalogue default (resolved below). A full
    // template (seed + skin + art — the usual payload) fully replaces the saved design; a partial
    // one overrides only the fields it carries and keeps the session's value for the rest.
    const wantSeedId = template?.siteSeedId ?? cd?.siteSeedId ?? null;
    const wantSkinId = template?.siteSkinId ?? cd?.siteSkinId ?? null;
    const wantArtStyleId = template?.artStyleId ?? cd?.artStyleId ?? null;

    const resolvedTheme =
      wantSeedId != null ? catalog.themes.find((t) => t.seedId === wantSeedId) ?? null : null;
    const theme = resolvedTheme ?? catalog.themes[0] ?? null;
    // Colour / style resolve against the matched theme only (a skin/style id is meaningless
    // without its theme), else fall back to that theme's first colour + first available style.
    const resolvedColor =
      resolvedTheme && wantSkinId != null
        ? theme?.colors.find((c) => Number(c.id) === wantSkinId) ?? null
        : null;
    const color = resolvedColor ?? theme?.colors[0] ?? null;
    const styles = availableStyles(catalog, theme, color);
    const resolvedStyle =
      resolvedTheme && wantArtStyleId != null
        ? styles.find((s) => Number(s.id) === wantArtStyleId) ?? null
        : null;
    const style = resolvedStyle ?? styles[0] ?? null;

    // A template actually drove the design → force the design-seed save to PERSIST this override,
    // even over a session that already had a design (that save skips a session with tokens by
    // default). Only when the template resolved a real theme (an unknown/empty template is a no-op).
    templateAppliedRef.current = template != null && resolvedTheme != null;

    // Consume the one-shot template hand-off now it's been read (whether it overrode the session
    // design or resolved to nothing) so it can't re-apply to a later brand. The resolved design
    // lives in `selection` below; the design-seed save persists it. Gated on `session.loaded`: on
    // the terminal-error branch the design is NEVER persisted (the seed-save effect early-returns
    // without a loaded session), so leave the durable keys intact for a later successful load.
    if (template && session.loaded) clearStartTemplate();

    // Engaged before? (picked a pack / edited a feature) — persisted per-session. A fresh /
    // unengaged session opens at the Basic baseline (all Off), ignoring the backend's default
    // pack so the user starts from a clean slate and explicitly chooses.
    const sid = session.sessionId;
    let seedEngaged = false;
    try {
      seedEngaged = !!sid && localStorage.getItem(ENGAGED_KEY(sid)) === '1';
    } catch {
      seedEngaged = false;
    }

    // On Custom before? (sticky; see CUSTOM_KEY). Re-seeded from localStorage so a returning user
    // stays on Custom even when their saved tiers coincide with a pack's defaults.
    let seedCustom = false;
    try {
      seedCustom = !!sid && localStorage.getItem(CUSTOM_KEY(sid)) === '1';
    } catch {
      seedCustom = false;
    }

    // Confirmed a pack via the chooser before? (one-way; see CONFIRMED_KEY). A returning user who
    // confirmed in a prior visit must NOT see the reveal again.
    let seedConfirmed = false;
    try {
      seedConfirmed = !!sid && localStorage.getItem(CONFIRMED_KEY(sid)) === '1';
    } catch {
      seedConfirmed = false;
    }

    // Feature tiers: start every feature Off, then overlay the saved gamification picks — but
    // ONLY when the user has engaged; otherwise the step stays at the all-Off baseline.
    const tiers: Record<number, number> = {};
    for (const f of catalog.features) {
      const off = offTierId(f);
      if (off != null) tiers[f.id] = off;
    }
    if (seedEngaged) {
      for (const it of cd?.items ?? []) {
        if (it.type !== CampaignItemType.GAMIFICATION_ITEM) continue;
        const tid = Number(it.tier_id);
        if (!Number.isNaN(tid) && catalog.features.some((f) => f.id === it.item_id)) tiers[it.item_id] = tid;
      }
    }

    setSelection({
      themeId: theme?.id ?? null,
      featuredThemeId: theme?.id ?? null,
      colorId: color?.id ?? null,
      styleId: style?.id ?? null,
    });
    setFeatureTiers(tiers);
    setEngaged(seedEngaged);
    setCustom(seedCustom);
    setHasConfirmedPack(seedConfirmed);
    // Operating-region precedence: a country already saved on the session (an explicit / prior
    // choice) always wins. Otherwise pre-select the region cued by the marketing site — the
    // explicit `or` (INT|MEX) when sent, else the UI-locale cue (e.g. Mexican-Spanish → Mexico) —
    // so the deploy location matches out of the box. This is the operating REGION only; the
    // session's pricing CURRENCY is a separate property (set at mint from `startProperties.currency`,
    // changed via the account-menu selector). Only applied when the region is real + selectable.
    if (cd?.countryCode) {
      setCountryCode(cd.countryCode);
    } else {
      const cued = preferredHandoffRegion();
      if (cued && regions.countries.some((c) => c.code === cued && c.isActive)) {
        setCountryCode(cued);
        // Hold recos until the surgical seed-save persists this region (backend prices
        // recos off the SAVED market); the seed-save's `.finally` releases the hold.
        recoHoldRef.current = true;
      } else if (!regions.countries.some((c) => c.code === DEFAULT_COUNTRY && c.isActive)) {
        // The configured default (DEFAULT_COUNTRY) isn't a live market: open on the first
        // live one so the picker highlights a selectable region, not a greyed, disabled
        // default. Treated like a cued region (held until the seed-save persists it so the
        // recommended-campaigns fetch prices off the saved market).
        const firstLive = regions.countries.find((c) => c.isActive)?.code;
        if (firstLive) {
          setCountryCode(firstLive);
          recoHoldRef.current = true;
        }
      }
    }
    // Resume the saved step-3 audience pick (no dirty: hydration must not re-save).
    if (cd?.traffic_option_id) setTrafficOptionIdState(cd.traffic_option_id);
    setStatus('ready');
  }, [
    catalog,
    regions.countries,
    session.loaded,
    session.error,
    session.loading,
    session.sessionId,
    session.data.configuration_data,
  ]);

  /** Select a theme, resetting colour + style to that theme's defaults. */
  const pickTheme = (id: string, promote: boolean): void => {
    if (!catalog) return;
    const theme = catalog.themes.find((t) => t.id === id);
    if (!theme) return;
    const color = theme.colors[0] ?? null;
    const style = availableStyles(catalog, theme, color)[0] ?? null;
    dirtyRef.current = true;
    setSelection((prev) => ({
      themeId: id,
      featuredThemeId: promote ? id : prev.featuredThemeId,
      colorId: color?.id ?? null,
      styleId: style?.id ?? null,
    }));
  };

  const setColor = (id: string): void => {
    if (!catalog) return;
    const theme = catalog.themes.find((t) => t.id === selection.themeId);
    const color = theme?.colors.find((c) => c.id === id) ?? null;
    // Styles depend on the colour, so reset the style when the colour changes.
    const style = availableStyles(catalog, theme ?? null, color)[0] ?? null;
    dirtyRef.current = true;
    setSelection((prev) => ({ ...prev, colorId: id, styleId: style?.id ?? null }));
  };

  const setStyle = (id: string): void => {
    dirtyRef.current = true;
    setSelection((prev) => ({ ...prev, styleId: id }));
  };

  // Step 3: persist the chosen traffic/audience option. Marks dirty so the save effect runs.
  const setTrafficOption = (id: string): void => {
    dirtyRef.current = true;
    setTrafficOptionIdState(id);
  };

  // Mark step 2 as user-configured (persisted per-session). Flips the step out of the Basic
  // baseline so the chosen pack / Custom selection shows instead of the "choose pack" card.
  const markEngaged = (): void => {
    setEngaged(true);
    const sid = session.sessionId;
    if (sid) {
      try {
        localStorage.setItem(ENGAGED_KEY(sid), '1');
      } catch {
        /* localStorage unavailable — engagement still holds for this session via state */
      }
    }
  };

  // Enter Custom (sticky). Persisted per-session so it survives reloads: a returning user who
  // switched to Custom stays on Custom even if their tiers match a pack. Implies engaged.
  const markCustom = (): void => {
    markEngaged();
    setCustom(true);
    const sid = session.sessionId;
    if (sid) {
      try {
        localStorage.setItem(CUSTOM_KEY(sid), '1');
      } catch {
        /* localStorage unavailable; Custom still holds for this session via state */
      }
    }
  };

  // Leave Custom only on an explicit pack/Basic application (the user's deliberate choice).
  const clearCustom = (): void => {
    setCustom(false);
    const sid = session.sessionId;
    if (sid) {
      try {
        localStorage.removeItem(CUSTOM_KEY(sid));
      } catch {
        /* localStorage unavailable; clearing the in-memory flag is enough */
      }
    }
  };

  // Latch "confirmed via the chooser" (one-way, persisted per-session). Called from the chooser's
  // apply funnel only — turns off the "Find my pack" reveal for good this session.
  const markConfirmed = (): void => {
    setHasConfirmedPack(true);
    const sid = session.sessionId;
    if (sid) {
      try {
        localStorage.setItem(CONFIRMED_KEY(sid), '1');
      } catch {
        /* localStorage unavailable; the confirmed flag still holds for this session via state */
      }
    }
  };

  // Changing any feature level is a manual edit → switch to (sticky) Custom.
  const setFeatureTier = (featureId: number, tierId: number): void => {
    dirtyRef.current = true;
    setPackageId(null);
    markCustom();
    setFeatureTiers((prev) => ({ ...prev, [featureId]: tierId }));
  };

  // Apply a package: set each feature to its bundle tier (others Off).
  const applyBundle = (bundle: Bundle): void => {
    if (!catalog) return;
    dirtyRef.current = true;
    markEngaged();
    clearCustom(); // applying a pack is an explicit choice → leave Custom
    setPackageId(bundle.id);
    setPickedIndex(catalog.bundles.findIndex((b) => b.id === bundle.id));
    setFeatureTiers(() => {
      const next: Record<number, number> = {};
      for (const f of catalog.features) {
        const item = bundle.items.find((it) => it.itemId === f.id);
        const id = item ? item.tierId : offTierId(f);
        if (id != null) next[f.id] = id;
      }
      return next;
    });
  };

  // "Basic" pack (mobile): no bundle, every feature at its Off tier.
  const applyBasicPack = (): void => {
    if (!catalog) return;
    dirtyRef.current = true;
    markEngaged();
    clearCustom(); // explicitly picking Basic leaves Custom
    setPackageId(null);
    setFeatureTiers(() => {
      const next: Record<number, number> = {};
      for (const f of catalog.features) {
        const id = offTierId(f);
        if (id != null) next[f.id] = id;
      }
      return next;
    });
  };

  // Commit a whole custom tier selection in one shot (single save). The map
  // is also remembered as "the" custom pack so its card can be re-selected.
  const applyCustomTiers = (tiers: Record<number, number>): void => {
    dirtyRef.current = true;
    markCustom();
    setPackageId(null);
    setFeatureTiers({ ...tiers });
    setCustomTiers({ ...tiers });
  };

  const setCountry = (code: string): void => {
    dirtyRef.current = true;
    setCountryCode(code);
    setCountryPickerOpen(false);
    // Recommendations are market-scoped — drop the stale list; the reco effect refetches
    // for the new market once the country-change save settles (recoCountryRef tracks it).
    dispatch(clearRecommendedCampaigns());
    // Re-price the session to the new market's currency so region, currency and payment stay
    // consistent (e.g. Mexico to MXN, which surfaces the SPEI rail). Only re-price to a currency
    // the catalogue flags `is_active` (exactly what `/set-currency` accepts); anything else is
    // left as-is, degrading to a display mismatch, never a stuck one-way session. `setCurrency`
    // also no-ops when the currency is unchanged.
    const nextCountry = regions.countries.find((c) => c.code === code) ?? null;
    if (nextCountry?.currency && isCatalogueActiveCurrency(nextCountry.currency)) {
      setCurrency(nextCountry.currency);
    }
  };

  // Account-menu currency selector. Currency is a session property priced server-side, so this
  // POSTs `/set-currency` (re-prices the bound campaigns + `configuration_data.currency`), then
  // drops + re-fetches the recommended campaigns so the pack chooser re-prices too (they are
  // priced in the session currency but fetched separately). Independent of the operating region.
  // A rejected call (e.g. a backend that still wants a numeric id) leaves the session unchanged.
  const setCurrency = (code: string, onApplied?: () => void): void => {
    if (!session.sessionId || code === currency) return;
    dispatch(sessionSetCurrency({ currency: code }))
      .unwrap()
      .then(() => {
        dispatch(clearRecommendedCampaigns());
        recoCountryRef.current = null; // force the reco effect to re-fetch for the new currency
        setRecoRetryTick((n) => n + 1);
        onApplied?.(); // only NOW is the switch actually applied server-side (caller may latch it)
      })
      .catch(() => {
        /* set-currency failed — leave the session (and its currency) as-is. */
      });
  };

  // Reconcile the session currency with the marketing hand-off, even on RESUME. `resolveMintCurrency`
  // fixes currency only on a FRESH mint, so an owner who first arrived from the International/USD site
  // and later returns from the BR/BRL site (same session/email) resumes the minted USD session and
  // would otherwise stay on USD. Once the catalogue + a REAL session currency are loaded, honor the
  // hand-off currency via `/set-currency` (both must be catalogue-active to switch). A durable
  // value-keyed latch (`readStartCurrencyApplied`) marks a hand-off currency as honored ONCE per
  // browser session — whether the mint already set it OR we switch to it now — so a later manual pick
  // in the account menu is respected across remounts, while arriving with a genuinely DIFFERENT
  // currency still switches. We read the REAL `configuration_data.currency` (not the mint-fallback
  // placeholder), so the effect never acts on an unsettled value.
  const handoffCurrencyRef = useRef(false);
  const sessionCurrency = session.data.configuration_data?.currency;
  useEffect(() => {
    if (handoffCurrencyRef.current || !configState.config || !sessionCurrency) return;
    const handoff = readStartMarketHandoff().currency;
    if (!handoff) return;
    handoffCurrencyRef.current = true;
    if (readStartCurrencyApplied() === handoff) return; // this hand-off already honored — respect a later manual change
    const next = handoffCurrencySwitch(handoff, sessionCurrency, isCatalogueActiveCurrency);
    if (!next) {
      // Already equal (the mint set it) or not catalogue-active (unswitchable): the hand-off is
      // settled, so latch it now — a later manual pick then sticks across remounts.
      markStartCurrencyApplied(handoff);
      return;
    }
    // A switch is needed: latch only AFTER `/set-currency` actually applies, so a transient failure
    // isn't durably latched (it retries on the next remount) while a successful switch is respected.
    setCurrency(next, () => markStartCurrencyApplied(handoff));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot per mount (handoffCurrencyRef) + durable per-session latch; re-checks only when the real session currency / catalogue change. setCurrency + isCatalogueActiveCurrency are render-fresh closures with stable behaviour.
  }, [sessionCurrency, configState.config]);

  // Apply the marketing hand-off OPERATING REGION on RESUME, even over a saved region. The seed above
  // pre-selects the region only when NONE is saved (a saved/prior choice wins), so an owner who first
  // onboarded in one region and later returns from a DIFFERENT-region marketing site (same
  // session/email) would otherwise stay in the old region. Once the seed has settled (`status` ready),
  // switch to the EXPLICIT hand-off `or` via `setCountry` when it is a live/selectable market that
  // differs from the current one — `setCountry` persists it + reprices currency + refetches recos, the
  // same path the region picker uses. A durable value-keyed latch (`readStartOrApplied`) honors a given
  // hand-off region once per browser session, so a later manual region pick sticks and a genuinely
  // different hand-off region still switches. Only the EXPLICIT `or` overrides a saved region; the UI
  // locale cue seeds the default above but never overrides a saved choice.
  const handoffOrRef = useRef(false);
  useEffect(() => {
    if (handoffOrRef.current || status !== 'ready') return;
    const handoffOr = readStartMarketHandoff().or;
    if (!handoffOr) return;
    handoffOrRef.current = true;
    if (readStartOrApplied() === handoffOr) return; // this hand-off region already honored — respect a later manual pick
    const target = regions.countries.find((c) => c.code === handoffOr && c.isActive);
    if (!target || handoffOr === countryCode) {
      // Already in that region, or it is not a live/selectable market: settled, so latch it (a later
      // manual pick then sticks) without switching.
      markStartOrApplied(handoffOr);
      return;
    }
    markStartOrApplied(handoffOr);
    setCountry(handoffOr);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot per mount (handoffOrRef) + durable per-session latch; re-checks only when the seed status / region / catalogue change. setCountry is a render-fresh closure with stable behaviour.
  }, [status, countryCode, regions.countries]);

  // Apply a whole campaign: set the session campaign server-side and sync the local
  // feature tiers to its defaults. NOTE: must NOT set `dirtyRef` — the thunk persists
  // items + campaignId itself, so a parallel `sessionSave` (built from stale tiers)
  // would race the set-recommended-campaign POST and clobber the result.
  const applyCampaign = (option: CampaignOption): void => {
    setPackageId(null);
    markConfirmed(); // pressing "Confirm package selection" (any option) latches the reveal off
    if (option.isCustom) {
      // Explicitly choosing the Custom card: the selection itself is unchanged (the card already
      // reflects it), but make sure we're on (and stick to) the Custom state.
      markCustom();
      return;
    }
    markEngaged();
    clearCustom(); // a pack (or Basic) was explicitly applied → leave Custom
    // Basic is the $0 baseline, but it is STILL a real backend campaign — bind it server-side exactly
    // like any other pack (a marketing hand-off can request it via `selected_package=basic`) so the
    // session carries a valid campaign object for the server-priced invoice, never an empty one. Its
    // default_items are all-Off / free-bundled, so `onBasicBaseline` keeps the step at $0. Never set
    // `dirtyRef` here: the thunk persists items + campaignId itself, so a parallel autosave built from
    // stale tiers would race the set-recommended-campaign POST and clobber it.
    if (session.sessionId) {
      dispatch(sessionSetRecommendedCampaign({ id: session.sessionId, campaignId: option.id }));
    }
    setFeatureTiers(campaignFeatureTiers(option.campaign));
  };

  // Resolve the current design selection to a concrete theme/colour/style; null until all
  // three are picked (so a save never persists a half-resolved design).
  const resolveDesign = (): { theme: Theme; color: ColorOption; style: ArtStyleOption } | null => {
    if (!catalog) return null;
    const theme = catalog.themes.find((t) => t.id === selection.themeId) ?? null;
    const color = theme?.colors.find((c) => c.id === selection.colorId) ?? null;
    const styles = theme && color ? availableStyles(catalog, theme, color) : [];
    const style = styles.find((s) => s.id === selection.styleId) ?? null;
    if (!theme || !color || !style) return null;
    return { theme, color, style };
  };

  // Build the full `configuration_data` payload for a `session/save` from the current
  // selection. `trafficId` is written only when set, so a not-yet-chosen step 3 can't
  // clobber a saved value with null. Shared by the autosave effect and `saveNow`.
  const buildConfigurationData = (trafficId: string | null): Record<string, unknown> | null => {
    if (!catalog) return null;
    const design = resolveDesign();
    if (!design) return null;
    const { theme, color, style } = design;

    // Carry over any non-gamification base items already on the session (e.g. the
    // one-time "Setup" BASE_ITEM); fall back to a default base item if none exists.
    const existingItems = session.data.configuration_data?.items ?? [];
    const baseItems = existingItems.filter((it) => it.type !== CampaignItemType.GAMIFICATION_ITEM);
    const effectiveBaseItems: DefaultItem[] = baseItems.length
      ? baseItems
      : [{ item_id: 1, type: CampaignItemType.BASE_ITEM, tier_id: 'BASIC' }];

    // Gamification tiers: keep every feature wherever the BOUND CAMPAIGN dictates — i.e. the
    // session's own gamification items (seeded by the mint / set-recommended-campaign, which is
    // backend-authoritative for what tier each pack allows). We must NOT force a feature to its Off
    // tier: a mandatory feature (e.g. Meta-Jackpot) has no valid Off in a pack, so overwriting it
    // with Off would persist a tier the campaign disallows and could break the server-priced invoice.
    // This flow skips the Features step, so `featureTiers` is only ever the all-Off placeholder and
    // must not override the campaign; a feature the session carries no item for falls back to its
    // catalog Off tier (matches v3, where the saved tiers mirror the campaign's items).
    const sessionGamTiers = new Map<number, number>();
    for (const it of existingItems) {
      if (it.type !== CampaignItemType.GAMIFICATION_ITEM) continue;
      const tid = Number(it.tier_id);
      if (!Number.isNaN(tid)) sessionGamTiers.set(Number(it.item_id), tid);
    }
    const gamificationItems: DefaultItem[] = catalog.features.map((f) => ({
      item_id: f.id,
      type: CampaignItemType.GAMIFICATION_ITEM,
      tier_id: sessionGamTiers.get(f.id) ?? offTierId(f) ?? 0,
    }));

    // Persist the applied voucher code so the backend re-evaluates it on every save (and reads it at
    // `/invoice`). Precedence, highest → lowest: (1) the user's own edit from the checkout review
    // (`userVoucherRef`, incl. '' to CLEAR); (2) the marketing-site hand-off voucher (a standalone
    // `?voucher_code=` URL param, or the one embedded in `startProperties`), which SUPERSEDES a
    // voucher already on the session so an abandoned-cart owner who returns with a NEW code overrides
    // the older one persisted at mint; (3) the session's own code (kept when there is no hand-off this
    // session, e.g. a later brand, so a single-use voucher never leaks to the next brand). `??` does
    // NOT swallow the empty string, so a user removal ('') is preserved and sent.
    const voucherCode =
      userVoucherRef.current ?? startVoucherRef.current ?? session.data.configuration_data?.voucher_code ?? null;

    return {
      ...session.data.configuration_data,
      // Send the code whenever there is a definite value to persist — INCLUDING '' to CLEAR a
      // previously-applied voucher the user removed. Omit only when there is genuinely none (null).
      ...(voucherCode != null ? { voucher_code: voucherCode } : {}),
      items: [...effectiveBaseItems, ...gamificationItems],
      siteSeedId: theme.seedId,
      siteSkinId: Number(color.id),
      artStyleId: Number(style.id),
      tokens: color.tokens,
      countryCode,
      ...(trafficId != null ? { traffic_option_id: trafficId } : {}),
      // Preserve a previously-saved email when the in-memory `userEmail` is wiped (e.g. the
      // user returns via the emailed link with no auth.user in memory) — overwriting it with
      // `undefined` would make the save reject with "No email address found" (session/save
      // sources the email from this field). Mirrors the fallback used for the exposed email.
      emailAddress: userEmail ?? session.data.configuration_data?.emailAddress ?? '',
      previewUrl: buildPreviewUrl(theme, color, style),
      lastStep: apiStepRef.current,
      stepsVisited: stepsVisitedRef.current,
      // Checkout funnels: SPEI (sub-steps, payer name, copy counts) and card (attempts, whether
      // details were entered, Stripe's failure reasons), plus the rail last selected. Each is
      // omitted entirely until something has been recorded, so a session that never opened the
      // checkout keeps the shape it had — and the spread above preserves whatever a previous
      // visit already persisted. CLONED, never the live ref: what goes into the store gets
      // deep-frozen, and a frozen accumulator can no longer be added to.
      ...(Object.keys(speiAccountingRef.current).length
        ? { spei_checkout: structuredClone(speiAccountingRef.current) }
        : {}),
      ...(Object.keys(stripeAccountingRef.current).length
        ? { stripe_checkout: structuredClone(stripeAccountingRef.current) }
        : {}),
      ...(paymentMethodRef.current ? { payment_method: paymentMethodRef.current } : {}),
    };
  };

  /**
   * The session has advanced out of the configuration half, so `configuration_data` can no longer
   * be persisted (see {@link SAVABLE_SESSION_STATUSES}). Set by the payment acknowledge, which
   * fires from within this wizard.
   *
   * A `null` status means "not loaded yet", NOT "advanced": saves are gated on `session.loaded`
   * separately and must not be blocked by an unknown status.
   */
  const sessionAdvanced = session.status != null && !SAVABLE_SESSION_STATUSES.has(session.status);

  // Persist the full configuration whenever a user-initiated selection changes, by
  // dispatching the app's `sessionSave` thunk against the live authenticated session.
  useEffect(() => {
    if (!dirtyRef.current || status !== 'ready' || !catalog) return;
    if (!session.sessionId || !session.loaded) return;
    // The session left the configuration half (the payment acknowledge advances it, from the
    // checkout or on entry): `/save` would reject the whole payload with INVALID_SESSION_STATE.
    if (sessionAdvanced) return;
    // Never save while another save / set-recommended-campaign is in flight (e.g. the cheapest-tier
    // bind): `sessionSave.pending` REPLACES `state.data` with this payload, so a stale autosave built
    // from the pre-bind session would revert the bound tier. `dirtyRef` stays true, so the edit
    // re-saves once loading clears (this effect re-runs on `session.loading`). Mirrors `saveNow`.
    if (session.loading) return;
    const configurationData = buildConfigurationData(trafficOptionId);
    if (!configurationData) return;

    dirtyRef.current = false;
    dispatch(
      sessionSave({
        id: session.sessionId,
        data: { ...session.data, configuration_data: configurationData },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, featureTiers, packageId, countryCode, trafficOptionId, status, catalog, session.sessionId, session.loaded, session.loading, sessionAdvanced]);

  // Explicitly persist the full configuration RIGHT NOW — wired to every "Continue" button
  // and step transition (desktop WizardLayout + the mobile flows) so each step is saved the
  // moment the user advances, not only when a value changes. Commits the DEFAULT audience
  // option when the user advances past Players without tapping one, and writes the latest
  // `lastStep`/`stepsVisited` so a resumed session lands on the right step.
  //
  // Held in a ref (reassigned every render) so the stable `saveNow` callback always runs
  // against the latest selection instead of a stale closure. Skipped while a save /
  // set-recommended-campaign request is in flight, so an explicit save can't race-clobber
  // that request — the in-flight op already persists the latest state.
  // Returns the dispatched save so a caller that MUST persist before doing something else can
  // await it (see `flushCheckout`); resolves to `undefined` when the save was skipped.
  const saveNowImplRef = useRef<() => Promise<unknown> | undefined>(() => undefined);
  saveNowImplRef.current = (): Promise<unknown> | undefined => {
    if (status !== 'ready' || !catalog || !session.sessionId || !session.loaded) return undefined;
    // Same boundary as the autosave above: a post-acknowledge session rejects the payload.
    if (sessionAdvanced) return undefined;
    if (session.loading) return undefined;
    const trafficId = trafficOptionId ?? playerOptions[0]?.id ?? null;
    const configurationData = buildConfigurationData(trafficId);
    if (!configurationData) return undefined;
    dirtyRef.current = false;
    return dispatch(
      sessionSave({
        id: session.sessionId,
        data: { ...session.data, configuration_data: configurationData },
      }),
    );
  };
  const saveNow = useCallback((): void => {
    void saveNowImplRef.current();
  }, []);

  // ── Voucher (checkout-review editable) ──────────────────────────────────────────────────────
  // Apply / replace / remove a voucher from the checkout review. Unlike `saveNow`, these dispatch
  // `sessionSave` DIRECTLY (never through `saveNowImplRef`, whose `session.loading` guard would
  // silently drop the request), so the round-trip is deterministic: the promise resolves with the
  // backend-echoed `voucher`, and the reducer captures it so `billing` (the "Discount Voucher"
  // line + `dueToday`) re-renders on its own. Held in a ref reassigned each render so the stable
  // callbacks always read the latest selection + session.
  const [voucherPending, setVoucherPending] = useState(false);
  const applyVoucherImplRef = useRef<(code: string | null) => Promise<VoucherResult>>(async () => ({
    applied: false,
    code: null,
    error: 'UNAVAILABLE',
  }));
  applyVoucherImplRef.current = async (code: string | null): Promise<VoucherResult> => {
    if (status !== 'ready' || !catalog || !session.sessionId || !session.loaded || sessionAdvanced) {
      return { applied: false, code: null, error: 'UNAVAILABLE' };
    }
    // Record the user's edit past the readiness guard (its bail leaves the ref untouched; the later
    // no-design bail can only fire off the checkout step, where the voucher control never renders).
    // `null` ⇒ remove: persist '' AND consume the one-shot marketing hand-off stash, so a provider
    // remount (reload / route re-entry) can't re-seed `startVoucherRef` and resurrect the code the
    // user just removed. A string ⇒ apply the trimmed code.
    userVoucherRef.current = code == null ? '' : code.trim();
    if (code == null) clearStartVoucherCode();
    const trafficId = trafficOptionId ?? playerOptions[0]?.id ?? null;
    const configurationData = buildConfigurationData(trafficId);
    if (!configurationData) return { applied: false, code: null, error: 'UNAVAILABLE' };
    dirtyRef.current = false;
    const result = await dispatch(
      sessionSave({ id: session.sessionId, data: { ...session.data, configuration_data: configurationData } }),
    ).unwrap();
    return interpretVoucher(result?.data?.voucher ?? null);
  };
  const runVoucher = useCallback(async (code: string | null): Promise<VoucherResult> => {
    setVoucherPending(true);
    try {
      return await applyVoucherImplRef.current(code);
    } catch {
      // A network / server failure — distinct from a rejected code, which RESOLVES (not throws).
      return { applied: false, code: null, error: 'NETWORK' };
    } finally {
      setVoucherPending(false);
    }
  }, []);
  const applyVoucher = useCallback((code: string): Promise<VoucherResult> => runVoucher(code), [runVoucher]);
  const removeVoucher = useCallback((): Promise<VoucherResult> => runVoucher(null), [runVoucher]);

  /* ── Checkout accounting ──────────────────────────────────────────────────────────────
   * Accumulated in refs (not state) because none of it drives rendering — it exists purely
   * to be persisted. `buildConfigurationData` reads the refs, so the accounting rides along on
   * whichever save happens next, and the debounce below guarantees one eventually does.
   *
   * The merge rules are pure functions in `@/utils/session-checkout-accounting`, shared with
   * v0-mx / v3-mx and unit-tested there. Ported here so base v0 can drive the shared
   * `useCheckout` cross-flow. */
  const speiAccountingRef = useRef<SpeiCheckoutAccounting>({});
  const stripeAccountingRef = useRef<StripeCheckoutAccounting>({});
  const paymentMethodRef = useRef<CheckoutProvider | null>(null);

  // Seed the accumulators from the session, once per session id. Without this a returning buyer's
  // counters restart at zero and the next save OVERWRITES what the previous visit recorded. The
  // clone is required, not defensive: the store deep-freezes what it holds, and a frozen
  // accumulator can no longer be added to.
  const accountingSeededRef = useRef<string | null>(null);
  useEffect(() => {
    const sid = session.sessionId;
    if (!sid || !session.loaded || accountingSeededRef.current === sid) return;
    accountingSeededRef.current = sid;
    const cd = session.data.configuration_data;
    speiAccountingRef.current = cd?.spei_checkout ? structuredClone(cd.spei_checkout) : {};
    stripeAccountingRef.current = cd?.stripe_checkout ? structuredClone(cd.stripe_checkout) : {};
    paymentMethodRef.current = cd?.payment_method ?? null;
  }, [session.sessionId, session.loaded, session.data.configuration_data]);

  const checkoutSaveTimer = useRef<number | null>(null);
  const clearCheckoutSaveTimer = useCallback((): void => {
    if (checkoutSaveTimer.current != null) {
      window.clearTimeout(checkoutSaveTimer.current);
      checkoutSaveTimer.current = null;
    }
  }, []);
  useEffect(() => () => clearCheckoutSaveTimer(), [clearCheckoutSaveTimer]);

  // Disarm a queued accounting save the moment the session leaves the configuration half. The
  // debounce can straddle that boundary (the buyer's "I've made the transfer", or a cleared card
  // charge, advances the session while a copy-counter save is still pending), and firing across
  // it would POST a `configuration_data` body the backend rejects outright.
  useEffect(() => {
    if (!sessionAdvanced) return;
    clearCheckoutSaveTimer();
    dirtyRef.current = false;
  }, [sessionAdvanced, clearCheckoutSaveTimer]);

  // Merge one event set into the accumulators. The pure mergers build a NEW object every time
  // rather than mutating in place: the previous value has been handed to `sessionSave`, and Redux
  // Toolkit deep-FREEZES what it stores.
  const applyCheckoutEvent = useCallback((event: CheckoutFlushEvent): void => {
    const now = new Date().toISOString();
    if (event.spei) {
      speiAccountingRef.current = applySpeiCheckoutEvent(speiAccountingRef.current, event.spei, now);
    }
    if (event.stripe) {
      stripeAccountingRef.current = applyStripeCheckoutEvent(stripeAccountingRef.current, event.stripe, now);
    }
    dirtyRef.current = true;
  }, []);

  // Arm the debounced save: a buyer copying five coordinates in a row costs ONE save.
  const scheduleCheckoutSave = useCallback((): void => {
    clearCheckoutSaveTimer();
    checkoutSaveTimer.current = window.setTimeout(() => {
      checkoutSaveTimer.current = null;
      void saveNowImplRef.current();
    }, 800);
  }, [clearCheckoutSaveTimer]);

  const recordCheckout = useCallback(
    (event: SpeiCheckoutEvent): void => {
      applyCheckoutEvent({ spei: event });
      scheduleCheckoutSave();
    },
    [applyCheckoutEvent, scheduleCheckoutSave],
  );

  const recordCardCheckout = useCallback(
    (event: StripeCheckoutEvent): void => {
      applyCheckoutEvent({ stripe: event });
      scheduleCheckoutSave();
    },
    [applyCheckoutEvent, scheduleCheckoutSave],
  );

  const setPaymentMethod = useCallback(
    (method: CheckoutProvider): void => {
      // Idempotent: re-selecting the rail the session already records is not a change, and must
      // not arm a save. The checkout calls this on every selection, including the one it makes for
      // itself when it restores the buyer's previous method on entry.
      if (paymentMethodRef.current === method) return;
      paymentMethodRef.current = method;
      dirtyRef.current = true;
      scheduleCheckoutSave();
    },
    [scheduleCheckoutSave],
  );

  const flushCheckout = useCallback(
    async (event: CheckoutFlushEvent): Promise<void> => {
      applyCheckoutEvent(event);
      // Cancel the pending debounce: this save supersedes it, and letting it fire afterwards would
      // re-POST the same body against a session that may have advanced by then.
      clearCheckoutSaveTimer();
      try {
        await saveNowImplRef.current();
      } catch {
        /* Accounting is a side-channel — a failed save must never block the buyer. */
      }
    },
    [applyCheckoutEvent, clearCheckoutSaveTimer],
  );

  // Seed the DESIGN (and the language-cued region) onto the session once when it was
  // never persisted (a user who accepts the catalog-default theme/colours never triggers
  // the dirty save above, so `configuration_data.tokens` stays unset). The post-payment
  // logo generator seeds its colours from those tokens — without them the logo step
  // silently produces nothing. `countryCode` rides along on the SAME save so the region
  // cued from the marketing-site locale is durable (survives reload, and reaches
  // onboarding/deploy) and so the recommended-campaigns fetch — held until now, since the
  // backend prices recos off the SAVED market — can run against the committed region. This
  // is a deliberately SURGICAL save (design + region only): it must NOT touch
  // `items`/`campaignId`, so it can't reset an unengaged session's pack. Returning users
  // (tokens already saved) are skipped — UNLESS a marketing-site template hand-off overrode the
  // design this mount (`templateAppliedRef`), in which case we persist the override over the
  // session's old design too (still design-only, so the pack/features are untouched).
  const seededDesignRef = useRef(false);
  useEffect(() => {
    if (seededDesignRef.current) return;
    if (status !== 'ready' || !catalog || !session.sessionId || !session.loaded) return;
    const cd = session.data.configuration_data;
    if (cd?.tokens != null && !templateAppliedRef.current) {
      seededDesignRef.current = true; // already persisted + no template override — nothing to seed
      recoHoldRef.current = false; // defensive: never leave the reco fetch stuck
      return;
    }
    const theme = catalog.themes.find((t) => t.id === selection.themeId) ?? null;
    const color = theme?.colors.find((c) => c.id === selection.colorId) ?? null;
    const styles = theme && color ? availableStyles(catalog, theme, color) : [];
    const style = styles.find((s) => s.id === selection.styleId) ?? null;
    if (!theme || !color || !style) return; // wait for a complete resolved selection

    seededDesignRef.current = true;
    // Release the reco hold (see recoHoldRef) once the region has settled, then re-run the
    // reco effect. Deliberately called on BOTH success and failure: on a (rare) seed-save
    // failure the session is already degraded (design + region didn't persist), but we must
    // still not strand the pack chooser empty forever — releasing lets recos load for the
    // session's persisted (default) market rather than never loading at all.
    const releaseRecoHold = (): void => {
      if (recoHoldRef.current) {
        recoHoldRef.current = false;
        setRecoRetryTick((n) => n + 1);
      }
    };
    dispatch(
      sessionSave({
        id: session.sessionId,
        data: {
          ...session.data,
          configuration_data: {
            ...cd,
            siteSeedId: theme.seedId,
            siteSkinId: Number(color.id),
            artStyleId: Number(style.id),
            tokens: color.tokens,
            previewUrl: buildPreviewUrl(theme, color, style),
            // Persist the resolved market (the language cue, or the default) alongside the
            // design so it's durable and the reco fetch can run against the saved region.
            countryCode,
          },
        },
      }),
    )
      .unwrap()
      .then(releaseRecoHold) // region persisted → recos fetch for the committed market
      .catch(releaseRecoHold); // degraded save-failure → still release (don't strand the chooser)
  }, [status, catalog, selection, countryCode, session.sessionId, session.loaded, session.data, dispatch]);

  // Campaign pack model (step 2 chooser): all available campaigns + a Custom entry.
  const sessionItems = useMemo<DefaultItem[]>(
    () => (session.data.configuration_data?.items as DefaultItem[] | undefined) ?? [],
    [session.data.configuration_data?.items],
  );
  // Until the user has engaged with step 2, ignore the backend's default pack binding so the
  // step opens with NO pack selected (the "choose pack" card) instead of the default Focused.
  const activeCampaignId = engaged ? session.data.configuration_data?.campaignId : undefined;
  // The chooser's pack list is EXACTLY what the recommended-campaigns API returns (the bound
  // campaign + the recommended packs), nothing injected. Basic is a real $0 campaign in that list
  // and survives an apply on its own, because `sessionSetRecommendedCampaign` leaves
  // `recommendedCampaigns` intact — so no client-side "keep Basic" fallback is needed.
  const mergedCampaigns = useMemo(
    () => mergeCampaigns(session.campaigns, session.recommendedCampaigns),
    [session.campaigns, session.recommendedCampaigns],
  );
  const activeCampaignRaw = useMemo(
    () => mergedCampaigns.find((c) => String(c.id) === String(activeCampaignId)),
    [mergedCampaigns, activeCampaignId],
  );

  // The price CATALOG = the union of every campaign's `items[].tiers[]`. The backend never
  // ships a standalone "Basic" campaign, but every commercial pack carries the SAME full
  // ladder of features at identical base prices — so the catalog (base price + availability)
  // is the merge of all of them: max base price (so a pack that zeroes a tier it disables
  // can't hide it) and `enabled` if ANY pack enables it. These are the per-feature prices the
  // user sees in the sidebar — base, undiscounted (pack discounts only fold into the pack's
  // bundle total in the footer), stable as the user freely builds a Custom config.
  const catalogPrices = useMemo(() => catalogTierPriceMap(mergedCampaigns), [mergedCampaigns]);

  // Per-tier enablement of the ACTIVE bound pack (mirrors the Spark engagement step, which
  // reads `enabled` off the bound campaign's own tier ladder). Drives which tiers are
  // selectable — unlike `catalogPrices`, which ORs enablement across EVERY pack and so
  // leaves almost everything selectable. Empty when no real pack is bound.
  const activeCampaignPrices = useMemo(() => campaignTierPriceMap(activeCampaignRaw), [activeCampaignRaw]);

  // "Basic" baseline = the selection costs $0 in features: every selected tier is Off OR priced at 0
  // (a tier the market bundles for FREE, e.g. Meta-Jackpot Plus). Basic is NOT literally all-Off — it
  // can include a free feature — so an 'off'-only test wrongly flips a just-applied Basic back to the
  // previously-bound pack (its free Meta-Jackpot reads as "on", so it never registers as Basic).
  // Derived from the LOCAL `featureTiers` + catalog union prices so it's immediate and survives reload
  // (no fake campaignId persisted). Gated on `engaged` so a fresh session still shows the promo.
  const onBasicBaseline = useMemo(() => {
    const feats = catalog?.features ?? [];
    if (!feats.length) return false;
    return feats.every((f) => {
      const selected = featureTiers[f.id];
      const tier = f.tiers.find((t) => t.id === selected);
      // Off (or an unset feature) is always free; a non-Off tier only counts toward the baseline if
      // the catalog prices it at 0 (bundled free) — otherwise it's a paid pick and this is Custom.
      if (!tier || isOff(tier.level)) return true;
      return (catalogPrices.get(`${f.id}:${selected}`)?.base ?? 0) === 0;
    });
  }, [catalog, featureTiers, catalogPrices]);
  // Basic baseline = engaged, a $0-feature selection, and NOT on the sticky Custom flag. A user who
  // manually reaches a $0 selection stays on Custom (only explicitly picking the Basic card makes it
  // Basic), so `custom` takes precedence.
  const isBasicSelected = engaged && onBasicBaseline && !custom;

  // Custom is an EXPLICIT, sticky state (see CUSTOM_KEY / markCustom), NOT a structural compare
  // against the bound campaign. The old derivation snapped back to a pack whenever a manual config
  // happened to equal that pack's tiers; now the user stays on Custom until they explicitly apply
  // a pack. Gated on `engaged` so a fresh/unengaged session still shows the "choose pack" promo.
  const isCustomPack = engaged && custom;

  // Price the catalog features from the campaign union (base prices, no per-feature discounts).
  // The catalog (`data/api.ts`) ships features WITHOUT prices — every price shown here comes
  // ONLY from the recommended-campaigns API, never from /configurator. A non-Off tier no pack
  // prices/enables is `available: false` → the UI renders it disabled and unselectable; Off is
  // always selectable (the "remove feature" choice). Until the recommended campaigns land the
  // tiers simply carry no price (the step shows structure without figures) rather than a stale
  // catalog price — so a price is never sourced from /configurator, even transiently.
  const features = useMemo<Feature[]>(() => {
    const baseFeatures = catalog?.features ?? [];
    if (catalogPrices.size === 0) return baseFeatures;
    // A real bound pack disables the tiers ITS own ladder marks `enabled: false`; Custom /
    // Basic / no-pack fall back to the catalog union, where every tier any pack offers stays
    // selectable. Base price always comes from the union (stable as the user builds a Custom).
    const useActivePack = !isCustomPack && !isBasicSelected && !!activeCampaignRaw;
    return baseFeatures.map((f) => ({
      ...f,
      tiers: f.tiers.map((t): FeatureTier => {
        const key = `${f.id}:${t.id}`;
        const cp = catalogPrices.get(key);
        const active = activeCampaignPrices.get(key);
        const enabled = useActivePack && active ? active.enabled : !!cp && cp.enabled;
        const available = isOff(t.level) || enabled;
        if (!available) {
          return { ...t, price: undefined, originalPrice: undefined, discountLabel: undefined, available: false };
        }
        return {
          ...t,
          price: cp?.base ?? 0,
          originalPrice: undefined,
          discountLabel: undefined,
          available: true,
        };
      }),
    }));
  }, [catalog, catalogPrices, activeCampaignPrices, isCustomPack, isBasicSelected, activeCampaignRaw]);

  // Sum of the selected (non-Off) tier prices — the step-2 footer figure for a Custom
  // (manual) selection, and the Custom-pack features total at step 4. Reads the
  // campaign-resolved `features` so it tracks exactly the per-tier prices shown.
  const catalogFeaturesTotal = useMemo(() => {
    let sum = 0;
    for (const f of features) {
      const tier = f.tiers.find((t) => t.id === featureTiers[f.id]);
      if (tier && !isOff(tier.level) && tier.price != null) sum += tier.price;
    }
    return sum;
  }, [features, featureTiers]);

  // Features total uses the LOCAL catalog sum (not the session's campaign-priced items) for a
  // Custom selection AND for the un-engaged baseline — until the user engages, the session
  // still carries the backend's default pack items, but the UI shows the all-Off baseline, so
  // the total must track the local (all-Off → $0) selection, not those stale items.
  const useLocalFeaturesTotal = isCustomPack || isBasicSelected || !engaged;

  // Step-4 billing = setup + features (features = catalog/local sum when Custom or un-engaged,
  // else the active pack's campaign-priced gamification). Reactive on the live session.
  const billing = useMemo<Billing | null>(
    () =>
      session.loaded
        ? computeBilling(session, currency, { isCustomPack: useLocalFeaturesTotal, catalogFeaturesTotal })
        : null,
    [session, currency, useLocalFeaturesTotal, catalogFeaturesTotal],
  );
  // The features-only total shown in the step-2 footer (same figure billing prices).
  const featuresTotal = billing ? billing.productFeatures : useLocalFeaturesTotal ? catalogFeaturesTotal : 0;

  const campaigns = useMemo<CampaignOption[]>(
    () => buildCampaignOptions(mergedCampaigns, activeCampaignRaw, isCustomPack, sessionItems, currency, lang),
    [mergedCampaigns, activeCampaignRaw, isCustomPack, sessionItems, currency, lang],
  );
  const activeCampaign = useMemo<CampaignOption | null>(() => {
    // Custom wins: it's the explicit, sticky state, so it takes precedence over a pack/Basic match.
    if (isCustomPack) return campaigns.find((c) => c.isCustom) ?? null;
    if (isBasicSelected) return campaigns.find((c) => c.isBasic) ?? null;
    return campaigns.find((c) => !c.isCustom && !c.isBasic && String(c.id) === String(activeCampaignId)) ?? null;
  }, [campaigns, isBasicSelected, isCustomPack, activeCampaignId]);

  // Bind a campaign on the session once the recommended-campaigns list loads. v0 (easy setup) skips the
  // Features step and ALWAYS binds the CHEAPEST recommended tier — overriding whatever tier the
  // marketing cpid / mint defaulted to (the cpid still drives ATTRIBUTION via `campaignPackId`, stamped
  // at mint; it does NOT pick the tier here). "Cheapest" is the lowest-priced recommended campaign, NOT
  // `featuresPrice === 0`: some campaign groups have no free tier — the tier NAMED "Basic" can still
  // cost (e.g. its mandatory Jackpot is $1 while every other feature is free) — so a $0 match would
  // bind nothing. This guarantees the server-priced `/invoice` always sees a real campaign object, and
  // v0 prices the bound tier from the SERVER (its real featuresPrice), never the phantom all-Off $0.
  // Waits for the reco fetch to SETTLE so the pick is made against PRICED data (the /load placeholder
  // reads featuresPrice 0 → wrong tier). Backend stays authoritative for tiers, pricing, and the bound
  // campaign object.
  const wantedCampaignRef = useRef<
    { cpid: string | null; cid: string | null; name: string | null } | null | undefined
  >(undefined);
  if (wantedCampaignRef.current === undefined) {
    const market = readStartMarketHandoff();
    const name = (readStartSelectedPackage() ?? '').trim().toLowerCase() || null;
    wantedCampaignRef.current =
      market.cpid || market.cid || name ? { cpid: market.cpid, cid: market.cid, name } : null;
  }
  const appliedPackRef = useRef(false);
  const applyCampaignRef = useRef(applyCampaign);
  applyCampaignRef.current = applyCampaign;
  useEffect(() => {
    if (appliedPackRef.current) return;
    if (status !== 'ready') return; // wait for the seed to hydrate + settle engagement
    // Never act while a session save is in flight (the region seed-save, or an apply's autosave) —
    // wait for it to settle so this can't race-clobber it.
    if (session.loading) return;
    if (!session.sessionId) return;

    // Consume any marketing hand-off so a pack GUID can't linger to a later brand. v0 IGNORES the
    // hand-off's tier (it always takes the cheapest below), but still clears the one-shot state.
    if (wantedCampaignRef.current && !startCampaignPreselectDone()) {
      markStartCampaignPreselectDone();
      clearStartSelectedPackage();
    }

    // A resumed session is already engaged on its tier (localStorage) and priced from the session's own
    // items via the seed — respect it, never re-bind.
    if (engaged) {
      appliedPackRef.current = true;
      return;
    }

    // Decide against PRICED reco data: the /load placeholder for a bound campaign reads featuresPrice 0
    // (match:false), so picking "cheapest" before the fetch settles could bind the wrong tier. Wait for
    // the settle (success → priced list merged; or retry-exhaustion so this can't hang; the effect
    // re-runs when `campaigns` / `recoRetryTick` change).
    const market = countryCode ?? '';
    const recosSettled =
      recoCountryRef.current != null || (recoAttemptsRef.current[market] ?? 0) >= RECO_MAX_ATTEMPTS;
    if (!recosSettled) return;

    // The cheapest recommended tier: `buildCampaignOptions` sorts ascending by price and appends the
    // synthesized Custom entry last, so the first non-Custom option is the cheapest real tier.
    const cheapest = campaigns.find((o) => !o.isCustom);
    if (!cheapest) {
      appliedPackRef.current = true; // no recommended tiers offered — nothing to bind
      return;
    }
    appliedPackRef.current = true;
    // Apply the cheapest tier via the SAME path the chooser uses: binds it server-side (overriding a
    // non-cheapest mint/cpid default), marks engaged, and adopts its feature tiers so v0 prices it from
    // the SERVER (its real featuresPrice), not the phantom all-Off $0.
    applyCampaignRef.current(cheapest);
  }, [status, engaged, session.loading, session.sessionId, campaigns, countryCode, recoRetryTick]);

  const value = useMemo<ConfiguratorContextValue>(() => {
    const themes = catalog?.themes ?? [];
    const selectedTheme = themes.find((t) => t.id === selection.themeId) ?? null;
    const featuredTheme = themes.find((t) => t.id === selection.featuredThemeId) ?? selectedTheme;
    const colors = selectedTheme?.colors ?? [];
    const selectedColor = colors.find((c) => c.id === selection.colorId) ?? null;
    const styles =
      catalog && selectedTheme && selectedColor ? availableStyles(catalog, selectedTheme, selectedColor) : [];
    const selectedStyle = styles.find((s) => s.id === selection.styleId) ?? null;
    // Forward the active UI language + the no-scrollbar flag so the framed preview
    // site loads in the studio's language and hides its own scrollbar (the `nsc` param
    // replaces the old over-sized-iframe clip). Live preview only; the persisted
    // `previewUrl` (buildConfigurationData / the design seed) is left lang- and nsc-free.
    const previewUrl = buildPreviewUrl(selectedTheme, selectedColor, selectedStyle, lang, true);
    const country = regions.countries.find((c) => c.code === countryCode) ?? null;

    return {
      status,
      themes,
      features,
      bundles: catalog?.bundles ?? [],
      playerOptions,
      trafficOptionId,
      setTrafficOption,
      selection,
      selectedTheme,
      featuredTheme,
      colors,
      selectedColor,
      styles,
      selectedStyle,
      previewUrl,
      featureTiers,
      packageId,
      pickedIndex,
      emailAddress: userEmail ?? session.data.configuration_data?.emailAddress ?? '',
      countries: regions.countries,
      countryGroups: regions.groups,
      country,
      countryCode,
      currency,
      currencySymbol: currencySymbol(currency),
      currencies,
      setCurrency,
      countryPickerOpen,
      openCountryPicker: () => setCountryPickerOpen(true),
      closeCountryPicker: () => setCountryPickerOpen(false),
      setCountry,
      setTheme: (id: string) => pickTheme(id, false),
      chooseTheme: (id: string) => pickTheme(id, true),
      setColor,
      setStyle,
      setFeatureTier,
      applyBundle,
      applyBasicPack,
      applyCustomTiers,
      customTiers,
      billing,
      featuresTotal,
      campaigns,
      activeCampaign,
      isCustomPack,
      hasConfirmedPack,
      applyCampaign,
      saveNow,
      applyVoucher,
      removeVoucher,
      voucherPending,
      recordCheckout,
      recordCardCheckout,
      setPaymentMethod,
      flushCheckout,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    status,
    catalog,
    features,
    playerOptions,
    trafficOptionId,
    selection,
    featureTiers,
    packageId,
    pickedIndex,
    customTiers,
    billing,
    featuresTotal,
    campaigns,
    activeCampaign,
    isCustomPack,
    hasConfirmedPack,
    session,
    userEmail,
    regions,
    currency,
    currencies,
    countryCode,
    countryPickerOpen,
    lang,
    saveNow,
    applyVoucher,
    removeVoucher,
    voucherPending,
    recordCheckout,
    recordCardCheckout,
    setPaymentMethod,
    flushCheckout,
  ]);

  return <ConfiguratorContext.Provider value={value}>{children}</ConfiguratorContext.Provider>;
}

export function useConfigurator(): ConfiguratorContextValue {
  const ctx = useContext(ConfiguratorContext);
  if (!ctx) {
    throw new Error('useConfigurator must be used within a ConfiguratorProvider');
  }
  return ctx;
}
